import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import { trackLevelComplete, trackLevelFailed, trackAssessmentComplete, trackQuestionAnswered } from '../services/analytics';
import { useAuth } from '../firebase/AuthContext';
import Navbar from '../components/Navbar';
import educadoLogo from '../images/educado_logo.png';
import ZigZagPath from '../components/ZigZagPath';
import Confetti from 'react-confetti';
import LevelCompletionModal from '../components/LevelCompletionModal';

// Helper function to parse and render text with [UNDERLINED]...[/UNDERLINED] tags
function parseUnderlinedText(text) {
  if (!text) return null;

  const parts = [];
  const regex = /\[UNDERLINED\]([\s\S]*?)\[\/UNDERLINED\]/g;
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }
    parts.push(
      <span key={`underlined-${key++}`} className="underline decoration-2 underline-offset-2">
        {match[1]}
      </span>
    );
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : text;
}

const Quiz = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, backendUserId } = useAuth();
  const [showExplanation, setShowExplanation] = useState(true);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Removed sessionId and setSessionId, no session tracking needed
  const [currentLevel, setCurrentLevel] = useState(0);
  const [completedLevels, setCompletedLevels] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [wrongQuestions, setWrongQuestions] = useState([]);
  const [allAnsweredQuestions, setAllAnsweredQuestions] = useState([]);
  const [levelScores, setLevelScores] = useState({});
  const [finalLevel, setFinalLevel] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showLevelCleared, setShowLevelCleared] = useState(false);
  const [clearedLevel, setClearedLevel] = useState(null);
  const [countdown, setCountdown] = useState(5);
  const countdownRef = useRef(null);
  const [nextLevelToLoad, setNextLevelToLoad] = useState(null);
  const [preloadStatus, setPreloadStatus] = useState('Preparing questions...');
  const [proceedDisabled, setProceedDisabled] = useState(true);
  const [showZigzag, setShowZigzag] = useState(false);
  const [zigzagTarget, setZigzagTarget] = useState(null);
  const [markedQuestions, setMarkedQuestions] = useState(new Set());
  const [dividerWidth, setDividerWidth] = useState(50);
  
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const questionStartTimeRef = useRef(null); // Track per-question time
  const dividerRef = useRef(null);
  const isDragging = useRef(false);
  const resultsSubmitted = useRef(false);
  const questionsLoadedRef = useRef(false);

  // Helper function to detect device type
  const getDeviceType = () => {
    const ua = navigator.userAgent;
    if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
    if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return 'mobile';
    return 'desktop';
  };

  // Helper function to detect browser
  const getBrowser = () => {
    const ua = navigator.userAgent;
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Edg')) return 'Edge';
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
    return 'Unknown';
  };

  // Helper function to calculate total text length of a question (question text + all options)
  const getQuestionTotalLength = (question) => {
    let totalLength = (question.question || '').length;
    if (question.options && typeof question.options === 'object') {
      Object.values(question.options).forEach(optionText => {
        totalLength += (optionText || '').length;
      });
    }
    return totalLength;
  };

  // Sort questions by total length (shorter first) - only for Level 1
  const sortQuestionsByLength = (questions) => {
    return [...questions].sort((a, b) => getQuestionTotalLength(a) - getQuestionTotalLength(b));
  };

  useEffect(() => {
    // Prevent double fetch (React StrictMode runs effects twice in dev)
    if (questionsLoadedRef.current) return;
    
    // Check if coming from progress screen
    if (location.state?.fromProgress && location.state?.currentLevel) {
      questionsLoadedRef.current = true;
      const level = location.state.currentLevel;
      const completed = location.state.completedLevels || [];
      setCurrentLevel(level);
      setCompletedLevels(completed);
      setShowExplanation(false);
      
      // Restore accumulated data from previous levels
      if (location.state.levelScores) {
        console.log('🔄 Restoring levelScores from navigation state:', location.state.levelScores);
        setLevelScores(location.state.levelScores);
      }
      if (location.state.wrongQuestions) {
        console.log('🔄 Restoring wrongQuestions from navigation state:', location.state.wrongQuestions.length, 'items');
        setWrongQuestions(location.state.wrongQuestions);
      }
      if (location.state.allAnsweredQuestions) {
        console.log('🔄 Restoring allAnsweredQuestions from navigation state:', location.state.allAnsweredQuestions.length, 'items');
        setAllAnsweredQuestions(location.state.allAnsweredQuestions);
      }
      
      // Fetch questions for the specified level
      const fetchLevelQuestions = async () => {
        setLoading(true);
        try {
          const levelData = await apiService.getLevelQuestions(level, 5);
          if (levelData.success) {
            loadLevelData(levelData);
          }
        } catch (error) {
          console.error('Failed to load level:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchLevelQuestions();
      return;
    }
    
    // Directly fetch questions for level 1 on mount
    questionsLoadedRef.current = true;
    const fetchInitialQuestions = async () => {
      setPreloadStatus('Preparing questions...');
      setProceedDisabled(true);
      try {
        const levelData = await apiService.getLevelQuestions(1, 5);
        if (levelData.success) {
          // Sort Level 1 questions by length (shorter first) to ease users into the assessment
          const sortedQuestions = sortQuestionsByLength(levelData.questions || []);
          setQuestions(sortedQuestions);
          setPreloadStatus('✓ Ready to start!');
          setProceedDisabled(false);
        } else {
          setPreloadStatus('⚠ Error loading questions.');
        }
      } catch (error) {
        setPreloadStatus('⚠ Error loading. Please refresh.');
      }
    };
    fetchInitialQuestions();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  useEffect(() => {
    if (timeRemaining > 0 && !showExplanation && questions.length > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            handleTimeUp();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [timeRemaining, showExplanation, questions]);

  // Handle divider dragging
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging.current || !dividerRef.current) return;
      const container = dividerRef.current.parentElement;
      const rect = container.getBoundingClientRect();
      const newWidth = ((e.clientX - rect.left) / rect.width) * 100;
      if (newWidth > 25 && newWidth < 75) setDividerWidth(newWidth);
    };
    const handleMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = "default";
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  // Removed initializeSession and all startTest usage

  const startQuiz = async () => {
    console.log('✨ startQuiz - BEGIN');
    setShowExplanation(false);
    setLoading(true);
    try {
      console.log('✨ startQuiz - Calling getLevelQuestions(1, 5)');
      const data = await apiService.getLevelQuestions(1, 5);
      console.log('✨ startQuiz - getLevelQuestions response:', data);
      if (data.success) {
        // Ensure UI reflects level 1
        setCurrentLevel(1);
        loadLevelData(data, 1);
        setSubmitting(false);
      }
    } catch (error) {
      console.error('Failed to load zone:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLevelData = (data, levelId = null) => {
    let questionsToLoad = data.questions;
    // Sort Level 1 questions by length (shorter first) to ease users into the assessment
    if (levelId === 1 || data.level === 1) {
      questionsToLoad = sortQuestionsByLength(data.questions);
    }
    setQuestions(questionsToLoad);
    setCurrentQuestionIndex(0);
    setAnswers([]);
    setSelectedOption(null);
    setTimeRemaining(data.time_minutes * 60);
    startTimeRef.current = Date.now();
    questionStartTimeRef.current = Date.now(); // Start per-question timer
    // Clear submitting flag so the Next/Submit button becomes active for the new level
    setSubmitting(false);
  };

  const selectOption = (key) => {
    setSelectedOption(key);
  };

  const nextQuestion = () => {
    if (!selectedOption) return;

    const question = questions[currentQuestionIndex];
    const isCorrect = selectedOption === question.correct_option;
    
    console.log('📝 nextQuestion - currentQuestionIndex:', currentQuestionIndex);
    console.log('📝 nextQuestion - questions.length:', questions.length);
    console.log('📝 nextQuestion - question:', question.question);
    console.log('📝 nextQuestion - selected:', selectedOption);
    console.log('📝 nextQuestion - correct:', question.correct_option);
    console.log('📝 nextQuestion - isCorrect:', isCorrect);

    const answerData = {
      question_id: question.id,
      question: question.question,
      selected: selectedOption,
      correct: question.correct_option,
      topic: question.topic,
      explanation: question.explanation,
      level: question.level,
      level_name: question.level_name
    };

    const newAnswers = [...answers, answerData];
    console.log('📝 nextQuestion - newAnswers.length:', newAnswers.length);
    console.log('📝 nextQuestion - newAnswers:', newAnswers);
    setAnswers(newAnswers);

    // Calculate time taken for this question
    const questionTimeTaken = Math.floor((Date.now() - (questionStartTimeRef.current || Date.now())) / 1000);
    // Reset question timer for next question
    questionStartTimeRef.current = Date.now();

    const newAllAnsweredQuestions = [...allAnsweredQuestions, {
      question_id: question.id,
      is_correct: isCorrect,
      topic: question.topic || question.sub_category,
      level: question.level,
      difficulty: question.difficulty,
      selected_option: selectedOption,
      correct_option: question.correct_option,
      time_taken: questionTimeTaken
    }];
    console.log('📝 nextQuestion - newAllAnsweredQuestions.length:', newAllAnsweredQuestions.length);
    setAllAnsweredQuestions(newAllAnsweredQuestions);

    let newWrongQuestions = wrongQuestions;
    if (!isCorrect) {
      newWrongQuestions = [...wrongQuestions, {
        question: question.question,
        topic: question.topic,
        selected: selectedOption,
        correct: question.correct_option,
        selected_text: question[`option_${selectedOption.toLowerCase()}`],
        correct_text: question[`option_${question.correct_option.toLowerCase()}`],
        explanation: question.explanation,
        level: question.level,
        level_name: question.level_name
      }];
      console.log('📝 nextQuestion - Added to wrong questions. newWrongQuestions.length:', newWrongQuestions.length);
      setWrongQuestions(newWrongQuestions);
    }

    if (currentQuestionIndex >= questions.length - 1) {
      // Pass the updated arrays directly to submitLevel
      console.log('📝 nextQuestion - Last question! Calling submitLevel');
      console.log('📝 nextQuestion - Passing newAnswers.length:', newAnswers.length);
      console.log('📝 nextQuestion - Passing newWrongQuestions.length:', newWrongQuestions.length);
      console.log('📝 nextQuestion - Passing newAllAnsweredQuestions.length:', newAllAnsweredQuestions.length);
      submitLevel(newAnswers, newWrongQuestions, newAllAnsweredQuestions);
    } else {
      console.log('📝 nextQuestion - Moving to next question');
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedOption(null);
    }
  };

  const submitLevel = async (answersToSubmit = null, wrongQuestionsToSubmit = null, allAnsweredQuestionsToSubmit = null) => {
    // Always use at least level 1 for submission
    const submitLevelId = currentLevel === 0 ? 1 : currentLevel;
    console.log('✅ submitLevel - BEGIN for level:', submitLevelId);
    if (timerRef.current) clearInterval(timerRef.current);
    setSubmitting(true);
    // Use provided arrays or fall back to state
    const finalAnswers = answersToSubmit || answers;
    const finalWrongQuestions = wrongQuestionsToSubmit || wrongQuestions;
    const finalAllAnsweredQuestions = allAnsweredQuestionsToSubmit || allAnsweredQuestions;
    try {
      const timeTaken = Math.floor((Date.now() - startTimeRef.current) / 1000);
      const userId = backendUserId || user?.uid || null;
      console.log('✅ submitLevel - userId:', userId);
      console.log('✅ submitLevel - backendUserId:', backendUserId);
      console.log('✅ submitLevel - Firebase UID:', user?.uid);
      console.log('✅ submitLevel - timeTaken:', timeTaken);
      console.log('✅ submitLevel - answers count:', finalAnswers.length);
      console.log('✅ submitLevel - Calling submitLevel API');
      const data = await apiService.submitLevel(submitLevelId, finalAnswers, timeTaken, userId);
      console.log('✅ submitLevel - submitLevel response:', data);
      if (data.success) {
        // Use the actual submitted level id (handles initial currentLevel === 0 case)
        const submittedLevel = submitLevelId;
        const newLevelScores = {
          ...levelScores,
          [submittedLevel]: {
            level: submittedLevel,
            level_name: data.level_name,
            score: data.correct,
            total: data.total,
            pass_score: Math.ceil(data.total * 0.8), // 80% passing
            passed: data.passed,
            time_taken: timeTaken
          }
        };
        console.log('✅ submitLevel - newLevelScores:', newLevelScores);
        console.log('✅ submitLevel - data.passed:', data.passed);
        console.log('✅ submitLevel - data.is_final:', data.is_final);
        console.log('✅ submitLevel - data.final_level:', data.final_level);
        setLevelScores(newLevelScores);
        setFinalLevel(data.final_level);

        // Track level completion
        if (data.passed) {
          trackLevelComplete(submittedLevel, data.level_name, data.correct, data.total);
        } else {
          trackLevelFailed(submittedLevel, data.level_name, data.correct);
        }

        // Only move to next level if passed AND not final
        // Otherwise (failed OR is final), go to results
        if (data.passed && data.is_final === false) {
          // Directly navigate to progress page with confetti and level result
          const nextLevel = submittedLevel + 1;
          console.log('✅ submitLevel - Level cleared, directly moving to next level', nextLevel);
          
          // Add current level to completed levels
          const newCompletedLevels = [...completedLevels, submittedLevel];
          
          // Navigate to progress screen with next level, previous level result, AND accumulated data
          navigate('/progress', { 
            state: { 
              currentLevel: nextLevel, 
              completedLevels: newCompletedLevels,
              previousLevelResult: {
                level: submittedLevel,
                levelName: data.level_name,
                score: data.correct,
                total: data.total,
                passed: true
              },
              showConfetti: true,
              // Pass accumulated data to preserve across navigation
              levelScores: newLevelScores,
              wrongQuestions: finalWrongQuestions,
              allAnsweredQuestions: finalAllAnsweredQuestions
            } 
          });
        } else {
          // Test complete - either failed or reached final level
          console.log('✅ submitLevel - Test complete, navigating to results');
          // Calculate correct achieved level: if passed, use final_level; if failed, subtract 1 (min 0)
          const achievedLevel = data.passed ? data.final_level : Math.max(0, submittedLevel - 1);
          console.log('✅ submitLevel - achievedLevel:', achievedLevel, '(passed:', data.passed, ')');
          
          // Track assessment completion
          const totalQuestionsAnswered = Object.values(newLevelScores).reduce((sum, s) => sum + s.total, 0);
          const levelNames = { 0: 'AI Noob', 1: 'AI Novice', 2: 'AI Explorer', 3: 'AI Practitioner', 4: 'AI Specialist', 5: 'AI Expert', 6: 'AI Master', 7: 'AI Guru' };
          trackAssessmentComplete(achievedLevel, levelNames[achievedLevel] || 'Unknown', totalQuestionsAnswered);
          
          navigateToResults(achievedLevel, newLevelScores, finalWrongQuestions, finalAllAnsweredQuestions);
        }
      }
    } catch (error) {
      console.error('Failed to submit level:', error);
      setSubmitting(false);
    }
  };

  const loadNextLevel = async (levelId) => {
    console.log('🔄 loadNextLevel - BEGIN for level:', levelId);
    setLoading(true);
    try {
      console.log('🔄 loadNextLevel - Calling getLevelQuestions');
      const data = await apiService.getLevelQuestions(levelId, 5);
      console.log('🔄 loadNextLevel - getLevelQuestions response:', data);
      if (data.success) {
        loadLevelData(data, levelId);
      }
    } catch (error) {
      console.error('Failed to load level:', error);
    } finally {
      setLoading(false);
      // Ensure submitting state is cleared when next level load finishes (success or fail)
      setSubmitting(false);
    }
  };

  const handleTimeUp = () => {
    submitLevel();
  };

  const navigateToResults = async (level, scores, wrongQuestionsArray = null, allAnsweredQuestionsArray = null) => {
    console.log('🏁 navigateToResults - BEGIN');
    
    // Use provided arrays or fall back to state
    const finalWrongQuestions = wrongQuestionsArray || wrongQuestions;
    const finalAllAnsweredQuestions = allAnsweredQuestionsArray || allAnsweredQuestions;
    
    // Prevent duplicate submissions
    if (resultsSubmitted.current) {
      console.log('⚠️ navigateToResults - Already submitted, skipping duplicate call');
      return;
    }
    resultsSubmitted.current = true;
    
    console.log('🏁 navigateToResults - level:', level);
    console.log('🏁 navigateToResults - scores:', scores);
    try {
      const userId = backendUserId || user?.uid || null;
      console.log('🏁 navigateToResults - userId:', userId);
      console.log('🏁 navigateToResults - backendUserId:', backendUserId);
      console.log('🏁 navigateToResults - Firebase UID:', user?.uid);
      console.log('🏁 navigateToResults - finalWrongQuestions count:', finalWrongQuestions.length);
      console.log('🏁 navigateToResults - finalAllAnsweredQuestions count:', finalAllAnsweredQuestions.length);
      console.log('🏁 navigateToResults - Calling getFinalResults');
      
      // Build more_details object with device info
      const moreDetails = {
        device: getDeviceType(),
        browser: getBrowser(),
        test_source: 'web_app'
      };
      
      const resultsResponse = await apiService.getFinalResults(
        level,
        finalWrongQuestions,
        scores,
        finalAllAnsweredQuestions,
        userId,
        moreDetails
      );
      console.log('🏁 navigateToResults - getFinalResults response:', resultsResponse);
      
      if (resultsResponse.success) {
        // Store both the raw data and the API response
        const resultsData = {
          final_level: level,
          level_name: resultsResponse.level_name,
          level_scores: scores,
          wrong_questions: finalWrongQuestions,
          all_answered_questions: finalAllAnsweredQuestions,
          topics_to_study: resultsResponse.topics_to_study,
          recommendations: resultsResponse.recommendations
        };
        sessionStorage.setItem('quizResults', JSON.stringify(resultsData));
        navigate('/results');
      }
    } catch (error) {
      console.error('Failed to get final results:', error);
      // Still navigate even if API fails, but without recommendations
      const resultsData = {
        final_level: level,
        level_scores: scores,
        wrong_questions: finalWrongQuestions,
        all_answered_questions: finalAllAnsweredQuestions
      };
      sessionStorage.setItem('quizResults', JSON.stringify(resultsData));
      navigate('/results');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimerClass = () => {
    if (timeRemaining <= 60) return 'timer danger';
    if (timeRemaining <= 120) return 'timer warning';
    return 'timer';
  };

  const currentQuestion = questions[currentQuestionIndex];
  const progress = questions.length > 0 ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0;

  const toggleMarkQuestion = () => {
    setMarkedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(currentQuestionIndex)) {
        newSet.delete(currentQuestionIndex);
      } else {
        newSet.add(currentQuestionIndex);
      }
      return newSet;
    });
  };

  const handleMoveNow = () => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    setShowLevelCleared(false);
    const lvl = nextLevelToLoad || (clearedLevel ? clearedLevel + 1 : currentLevel + 1);
    
    // Add current level to completed levels
    const newCompletedLevels = [...completedLevels, clearedLevel];
    
    // Navigate to progress screen with next level
    navigate('/progress', { 
      state: { 
        currentLevel: lvl, 
        completedLevels: newCompletedLevels 
      } 
    });
  };

  const mm = Math.floor(timeRemaining / 60).toString().padStart(2, "0");
  const ss = (timeRemaining % 60).toString().padStart(2, "0");

  const levelName = (
    (questions && questions.length > 0 && questions[0].level_name) ||
    (levelScores && levelScores[currentLevel] && levelScores[currentLevel].level_name) ||
    `Level ${currentLevel === 0 ? 1 : currentLevel}`
  );

  if (showExplanation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8 md:p-10 border border-gray-200">
          <div className="flex justify-center mb-6">
            <img src={educadoLogo} alt="Educado AI" className="h-10 w-auto" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-5">Welcome to the Educado AI Learning Test!</h1>
          <div className="space-y-5">
            <p className="text-base md:text-lg text-gray-700 text-center leading-relaxed">
              This test is designed to assess your knowledge of <strong className="text-primary">Artificial Intelligence</strong> across{' '}
              <span className="font-semibold text-primary">7 levels</span>.
            </p>
            
            <div className="bg-primary/5 border-l-4 border-primary rounded-r-lg p-5">
              <div className="flex items-start gap-3">
                <span className="text-2xl">💡</span>
                <p className="text-gray-700 leading-relaxed">
                  Score at least <strong className="text-primary">80%</strong> in each level to advance to the next level. 
                  At the end, you'll receive your final level along with personalized study recommendations.
                </p>
              </div>
            </div>
            
            <p className="text-lg md:text-xl text-center font-medium text-gray-800">Good luck! 🚀</p>
          </div>
          
          <button 
            className="mt-8 w-full bg-primary text-white py-3.5 px-6 rounded-lg font-semibold text-base flex items-center justify-center gap-2 hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed" 
            onClick={startQuiz} 
            disabled={proceedDisabled}
          >
            <span>Proceed to Test</span>
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>
          
          <p className="mt-5 text-center text-sm text-gray-600 flex items-center justify-center gap-2">
            <span className="inline-block w-2 h-2 bg-primary rounded-full animate-pulse"></span>
            {preloadStatus}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen w-screen bg-gray-50 m-0 p-0 overflow-hidden">
      <Navbar />

      {/* Timer and Progress Bar */}
      <div className="bg-white border-b border-gray-200 py-3 px-4 flex-shrink-0 shadow-sm">
        <div className="flex justify-center items-center mb-2.5 relative">
          <div className="absolute left-0 text-sm text-gray-600 font-medium">
            Question {currentQuestionIndex + 1} of {questions.length}
          </div>
            {/* Level name placed at the right side of the timer */}
            <div className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-600 font-semibold text-right">
              {levelName}
            </div>
          <div 
            className={`text-base font-bold font-mono ${
              timeRemaining <= 60 
                ? 'text-red-600' 
                : timeRemaining <= 120 
                  ? 'text-amber-500' 
                  : 'text-primary'
            }`}
          >
            {mm}:{ss}
          </div>
        </div>
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-primary transition-all duration-300 ease-out" 
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>

      {/* Main Content Area - Split Pane */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center flex-col gap-4">
          <div className="border-4 border-gray-200 border-t-primary rounded-full w-12 h-12 animate-spin"></div>
          <p className="text-gray-600">Loading questions...</p>
        </div>
      ) : currentQuestion ? (
        <div className="flex-1 flex relative overflow-hidden">
          {/* Left Pane - Question */}
          <div 
            className="overflow-auto p-6 bg-white border-r border-gray-200" 
            style={{ width: `${dividerWidth}%` }}
          >
            {/* Question Number */}
            <div className="mb-4 flex items-center gap-3">
              <span className="px-2.5 py-1 bg-gray-900 text-white rounded-md text-xs font-semibold">
                {currentQuestionIndex + 1}
              </span>
            </div>

            {/* Question Topic */}
            {currentQuestion.topic && (
              <div className="text-xs text-gray-500 font-semibold mb-3 uppercase tracking-wider">
                {currentQuestion.topic}
              </div>
            )}

            {/* Question Text */}
            <div className="text-base text-gray-900 leading-relaxed mb-6">
              {parseUnderlinedText(currentQuestion.question)}
            </div>

            {/* Image if available */}
            {currentQuestion.image_url && (
              <div className="mb-6">
                <img 
                  src={currentQuestion.image_url} 
                  alt="Question illustration" 
                  className="max-w-full h-auto rounded-lg border border-gray-200 shadow-sm"
                />
              </div>
            )}

            <div className="h-20"></div>
          </div>

          {/* Resizable Divider */}
          <div
            ref={dividerRef}
            onMouseDown={() => {
              isDragging.current = true;
              document.body.style.cursor = "ew-resize";
            }}
            className="w-1.5 bg-gray-300 cursor-ew-resize relative flex-shrink-0 hover:bg-gray-400 transition-colors"
            style={{ transition: isDragging.current ? 'none' : 'background-color 0.2s' }}
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-10 bg-gray-500 rounded-sm"></div>
          </div>

          {/* Right Pane - Answer Options */}
          <div className="flex-1 overflow-auto p-6 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">
              {currentQuestion.is_spr_candidate ? 'Enter your answer' : 'Select your answer'}
            </h3>

            {currentQuestion.is_spr_candidate ? (
              /* Numeric Input for SPR Questions */
              <div>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*\.?[0-9]*"
                  value={selectedOption || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^-?\d*\.?\d*$/.test(val)) {
                      setSelectedOption(val);
                    }
                  }}
                  placeholder="Your response"
                  className="w-full max-w-sm px-4 py-3 text-base border-2 border-gray-300 rounded-lg outline-none transition-all focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
                <p className="mt-2 text-xs text-gray-600">
                  Enter a numeric value (decimals allowed)
                </p>
              </div>
            ) : (
              /* Multiple Choice Options */
              <div className="flex flex-col gap-3">
                {['A', 'B', 'C', 'D'].map((key) => {
                  const isSelected = selectedOption === key;
                  const optionText = currentQuestion[`option_${key.toLowerCase()}`];
                  
                  if (!optionText) return null;

                  return (
                    <label
                      key={key}
                      className={`block border-2 rounded-lg p-4 cursor-pointer transition-all ${
                        isSelected 
                          ? 'border-primary bg-primary/5 shadow-sm' 
                          : 'border-gray-300 bg-white hover:bg-gray-50 hover:border-gray-400'
                      }`}
                    >
                      <input
                        type="radio"
                        name="choice"
                        value={key}
                        checked={isSelected}
                        onChange={() => selectOption(key)}
                        className="hidden"
                      />
                      <div className="flex items-start gap-3">
                        <div className={`h-6 w-6 flex items-center justify-center rounded-full border-2 font-semibold text-sm flex-shrink-0 ${
                          isSelected 
                            ? 'border-primary bg-primary text-white' 
                            : 'border-gray-400 bg-white text-gray-700'
                        }`}>
                          {key}
                        </div>
                        <div className="text-sm text-gray-900 leading-relaxed pt-0.5">
                          {parseUnderlinedText(optionText)}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}

            {/* Next Button */}
            <button
              onClick={nextQuestion}
              disabled={!selectedOption || submitting}
              className={`mt-6 px-6 py-3 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                selectedOption && !submitting
                  ? 'bg-primary text-white cursor-pointer hover:bg-primary-dark shadow-md hover:shadow-lg'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
              }`}
            >
              {submitting ? (
                <>
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Submitting...</span>
                </>
              ) : (
                <span>{currentQuestionIndex >= questions.length - 1 ? 'Submit Zone' : 'Next Question →'}</span>
              )}
            </button>

            <div className="h-20"></div>
          </div>
        </div>
      ) : null}
      
      {/* Confetti on level completion */}
      {showLevelCleared && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false}
          numberOfPieces={500}
          gravity={0.3}
        />
      )}
      
      {/* Level Completion Modal */}
      <LevelCompletionModal
        isOpen={showLevelCleared}
        level={clearedLevel}
        score={levelScores[clearedLevel]?.score || 0}
        totalQuestions={levelScores[clearedLevel]?.total || 5}
        onNextLevel={handleMoveNow}
        isLastLevel={false}
      />
      
      {showZigzag && (
        <div className="fixed inset-0 z-60 flex items-center justify-center pointer-events-none">
          <div className="bg-transparent p-4 rounded-lg pointer-events-none">
            <ZigZagPath levels={8} target={zigzagTarget} highlightColor={'var(--purple-5)'} spacing={92} nodeR={12} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Quiz;
